import { useEffect } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "@/components/Breadcrumbs";
import PageSchema from "@/components/PageSchema";
import LanguageSwitchLink from "@/components/LanguageSwitchLink";
import { setPageMeta, setHreflang } from "@/lib/seo";

const EN_URL = "/trailer-guides/gvwr-payload";
const ES_URL = "/es/gvwr-capacidad-de-carga";

export default function GvwrCapacidadDeCargaPage() {
  useEffect(() => {
    document.documentElement.lang = "es";
    setPageMeta(
      "GVWR y Capacidad de Carga: Guía de Pesos del Remolque | Juan The Trailer Guy",
      "Entiende el GVWR, el payload, la capacidad de los ejes, el peso de la lengüeta y el derating para no sobrecargar tu remolque.",
      ES_URL,
    );
    const cleanup = setHreflang([
      { lang: "en-US", href: EN_URL },
      { lang: "es-US", href: ES_URL },
      { lang: "x-default", href: EN_URL },
    ]);
    return () => {
      document.documentElement.lang = "en";
      cleanup();
    };
  }, []);

  return (
    <main className="bg-background text-foreground">
      <PageSchema
        name="GVWR y Capacidad de Carga: Guía de Pesos del Remolque"
        description="Entiende el GVWR, el payload, la capacidad de los ejes, el peso de la lengüeta y el derating para no sobrecargar tu remolque."
      />
      <section className="bg-zinc-900 text-white">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-10">
          <Breadcrumbs
            variant="dark"
            className="mb-5"
            items={[{ name: "Inicio", href: "/" }, { name: "Guías Educativas" }, { name: "GVWR y Capacidad de Carga" }]}
          />
          <p className="text-sm uppercase tracking-[0.2em] text-red-400 font-semibold">Pesos y Capacidad</p>
          <h1 className="mt-4 text-4xl md:text-6xl font-black uppercase leading-tight">
            GVWR y Capacidad de Carga Explicados
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-zinc-300">
            Lee la etiqueta, haz las cuentas, y no sobrecargues tu remolque. Aquí explicamos el GVWR, el
            payload (carga útil), la capacidad de los ejes, el peso de la lengüeta y el derating en
            lenguaje sencillo.
          </p>
          <div className="mt-6">
            <LanguageSwitchLink href={EN_URL} label="Read this guide in English" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 py-14 text-lg leading-8 text-muted-foreground">
        <h2 className="text-2xl font-black uppercase text-foreground">¿Qué es el GVWR?</h2>
        <p className="mt-4">
          GVWR significa <strong>Gross Vehicle Weight Rating</strong> — el peso máximo que el remolque
          (o la troca) puede pesar de forma segura una vez cargado, incluyendo el peso del propio
          remolque. Este número lo pone el fabricante y aparece en una etiqueta (VIN plate) en el
          remolque. Nunca es una sugerencia — es el límite legal y de seguridad.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">GVWR vs Peso en Vacío vs Payload</h2>
        <p className="mt-4">Estos tres números trabajan juntos, y confundirlos es el error más común:</p>
        <ul className="mt-4 space-y-2">
          <li>• <strong className="text-foreground">Peso en vacío (empty weight / curb weight):</strong> cuánto pesa el remolque solo, sin carga</li>
          <li>• <strong className="text-foreground">GVWR:</strong> el peso máximo total permitido, remolque más carga</li>
          <li>• <strong className="text-foreground">Payload (carga útil):</strong> GVWR menos el peso en vacío — esto es lo que realmente puedes cargar</li>
        </ul>
        <p className="mt-4">
          Por ejemplo: si un remolque tiene un GVWR de 14,000 lbs y pesa 3,000 lbs vacío, tu payload real
          es de 11,000 lbs. Ese es el número que debes comparar contra el peso de tu carga, no el GVWR.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">Capacidad de los Ejes</h2>
        <p className="mt-4">
          Cada eje también tiene su propia capacidad máxima (axle rating). La suma de la capacidad de
          todos los ejes debe ser igual o mayor al GVWR del remolque. Si distribuyes mal la carga —
          mucho peso hacia adelante o hacia atrás — puedes sobrecargar un eje incluso si el peso total
          está dentro del GVWR.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">Peso de la Lengüeta (Tongue Weight)</h2>
        <p className="mt-4">
          El peso de la lengüeta es cuánto peso empuja hacia abajo sobre el enganche de tu troca.
          Generalmente debe ser entre el 10% y el 15% del peso total cargado del remolque. Muy poco peso
          en la lengüeta puede causar que el remolque se balancee (trailer sway); demasiado puede
          sobrecargar el eje trasero de tu troca.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">¿Qué es el Derating?</h2>
        <p className="mt-4">
          El derating pasa cuando la capacidad real combinada de los componentes (ejes, llantas, frame)
          es menor que lo que el GVWR anunciado sugiere, o cuando se reduce la capacidad por combinación
          de factores — por ejemplo, llantas con menor capacidad de carga que los ejes. Siempre revisa
          que todos los componentes (ejes, llantas, frame) sean consistentes con el GVWR que anuncia el
          remolque, no solo la etiqueta general.
        </p>

        <h2 className="mt-10 text-2xl font-black uppercase text-foreground">Cómo Evitar Sobrecargar tu Remolque</h2>
        <ul className="mt-4 space-y-2">
          <li>• Pesa tu carga real, no la estimes</li>
          <li>• Compara contra el payload real (GVWR menos peso en vacío), no contra el GVWR</li>
          <li>• Revisa también las capacidades de tu troca (towing capacity y payload de la troca)</li>
          <li>• Deja un margen de seguridad — no cargues al límite exacto todos los días</li>
          <li>• Si no sabes si necesitas CDL para tu combinación, revisa nuestra{" "}
            <Link to="/es/licencia-cdl-remolques" className="font-semibold text-primary underline">
              guía de CDL y remolcado
            </Link>
          </li>
        </ul>
      </section>

      <section className="bg-muted/50">
        <div className="mx-auto max-w-7xl px-6 py-16 md:px-10">
          <h2 className="text-3xl font-black uppercase">Sigue Aprendiendo</h2>
          <div className="mt-6 text-primary font-semibold space-y-2">
            <Link to="/es/licencia-cdl-remolques" className="block hover:underline">→ CDL y Remolcado</Link>
            <Link to="/es/remolques-de-volteo" className="block hover:underline">→ Tráileres de Volteo Explicadas</Link>
            <Link to="/es/remolque-volteo-14k-vs-16k" className="block hover:underline">→ Tráiler de Volteo 14K vs 16K</Link>
            <Link to="/trailer-payload-calculator" className="block hover:underline">→ Calculadora de GVWR y Carga (en inglés)</Link>
          </div>
        </div>
      </section>
    </main>
  );
}
